const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const getToken = () => sessionStorage.getItem('token');

const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

// AUTH
export const login = async (email, password) => {
  const res = await fetch(`${BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  sessionStorage.setItem('token', data.token);
  sessionStorage.setItem('user', JSON.stringify(data.user));
  return data;
};

export const register = async (name, email, password, role = 'AGENT', inviteToken = null, organizationName = null) => {
  const res = await fetch(`${BASE_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password, role, inviteToken, organizationName }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  sessionStorage.setItem('token', data.token);
  sessionStorage.setItem('user', JSON.stringify(data.user));
  return data;
};

export const logout = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
};

export const getGoogleClientId = async () => {
  const res = await fetch(`${BASE_URL}/auth/google/client-id`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const loginWithGoogle = async (code, redirectUri) => {
  const res = await fetch(`${BASE_URL}/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirectUri }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  sessionStorage.setItem('token', data.token);
  sessionStorage.setItem('user', JSON.stringify(data.user));
  return data;
};

// CUSTOMERS
export const getCustomers = async () => {
  const res = await fetch(`${BASE_URL}/customers`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.customers;
};

export const createCustomer = async (customer) => {
  const res = await fetch(`${BASE_URL}/customers`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(customer),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

// TICKETS
export const getTickets = async () => {
  const res = await fetch(`${BASE_URL}/tickets`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.tickets;
};

export const createTicket = async (ticket) => {
  const res = await fetch(`${BASE_URL}/tickets`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(ticket),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const updateTicket = async (id, updates) => {
  const res = await fetch(`${BASE_URL}/tickets/${id}`, {
    method: 'PATCH',
    headers: headers(),
    body: JSON.stringify(updates),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

// MESSAGES
export const getMessages = async (ticketId) => {
  const res = await fetch(`${BASE_URL}/messages/ticket/${ticketId}`, {
    headers: headers(),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.messages;
};

export const sendMessage = async (ticketId, body, senderType = 'AGENT') => {
  const res = await fetch(`${BASE_URL}/messages`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ ticketId, body, senderType }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

// TASKS
export const getTasks = async ({ contactId } = {}) => {
  const params = new URLSearchParams();
  if (contactId) params.set('contactId', contactId);
  const res = await fetch(`${BASE_URL}/tasks?${params}`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.tasks;
};

export const createTask = async (task) => {
  const res = await fetch(`${BASE_URL}/tasks`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(task),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const updateTask = async (id, updates) => {
  const res = await fetch(`${BASE_URL}/tasks/${id}`, {
    method: 'PATCH',
    headers: headers(),
    body: JSON.stringify(updates),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const deleteTask = async (id) => {
  const res = await fetch(`${BASE_URL}/tasks/${id}`, {
    method: 'DELETE',
    headers: headers(),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.message);
  }
};

// USERS
export const getUsers = async () => {
  const res = await fetch(`${BASE_URL}/users`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.users;
};

// AI
export const aiSuggestReplies = async ({ subject, customerName, conversation }) => {
  const res = await fetch(`${BASE_URL}/ai/suggest`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ subject, customerName, conversation }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.suggestions;
};

export const aiSummarize = async ({ subject, conversation }) => {
  const res = await fetch(`${BASE_URL}/ai/summarize`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ subject, conversation }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.summary;
};

export const aiDetectPriority = async ({ subject, description }) => {
  const res = await fetch(`${BASE_URL}/ai/priority`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ subject, description }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.priority;
};

export const aiRouteTicket = async ({ subject, description, agents }) => {
  const res = await fetch(`${BASE_URL}/ai/route`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ subject, description, agents }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const aiTranslate = async ({ text }) => {
  const res = await fetch(`${BASE_URL}/ai/translate`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ text }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

// EMAIL
export const sendEmail = async ({ to, subject, text, ticketId }) => {
  const res = await fetch(`${BASE_URL}/email/send`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ to, subject, text, ticketId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

// PUBLIC CONTACT FORM (marketing site — no auth required)
export const submitContactForm = async ({ name, email, company, message }) => {
  const res = await fetch(`${BASE_URL}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, company, message }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

// INVITES
export const generateInvite = async () => {
  const res = await fetch(`${BASE_URL}/invites/generate`, {
    method: 'POST',
    headers: headers(),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const validateInvite = async (token) => {
  const res = await fetch(`${BASE_URL}/invites/validate/${token}`);
  const data = await res.json();
  return data;
};



// ORGANIZATION
export const getOrganization = async () => {
  const res = await fetch(`${BASE_URL}/organization`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

// GMAIL OAUTH
export const getGmailAuthUrl = async () => {
  const res = await fetch(`${BASE_URL}/gmail/auth`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const getGmailStatus = async () => {
  const res = await fetch(`${BASE_URL}/gmail/status`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const disconnectGmail = async () => {
  const res = await fetch(`${BASE_URL}/gmail/disconnect`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const syncGmail = async () => {
  const res = await fetch(`${BASE_URL}/gmail/sync`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const updateCustomer = async (id, data) => {
  const res = await fetch(`${BASE_URL}/customers/${id}`, {
    method: 'PATCH',
    headers: headers(),
    body: JSON.stringify(data),
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.message);
  return resData;
};

export const getCustomerNotes = async (customerId) => {
  const res = await fetch(`${BASE_URL}/customers/${customerId}/notes`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.notes;
};

export const createCustomerNote = async (customerId, content) => {
  const res = await fetch(`${BASE_URL}/customers/${customerId}/notes`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ content }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const deleteCustomerNote = async (customerId, noteId) => {
  const res = await fetch(`${BASE_URL}/customers/${customerId}/notes/${noteId}`, {
    method: 'DELETE',
    headers: headers(),
  });
  if (!res.ok) { const data = await res.json(); throw new Error(data.message); }
};

export const getCannedResponses = async () => {
  const res = await fetch(`${BASE_URL}/canned-responses`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return Array.isArray(data) ? data : Array.isArray(data.cannedResponses) ? data.cannedResponses : [];
};

// ── Contacts ──────────────────────────────────────────────────────
export const getContacts = async ({ search = '', status = 'ALL' } = {}) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (status && status !== 'ALL') params.set('status', status);
  const res = await fetch(`${BASE_URL}/contacts?${params}`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.contacts;
};

export const createContact = async (body) => {
  const res = await fetch(`${BASE_URL}/contacts`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const updateContact = async (id, body) => {
  const res = await fetch(`${BASE_URL}/contacts/${id}`, { method: 'PATCH', headers: headers(), body: JSON.stringify(body) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const deleteContact = async (id) => {
  const res = await fetch(`${BASE_URL}/contacts/${id}`, { method: 'DELETE', headers: headers() });
  if (!res.ok) { const data = await res.json(); throw new Error(data.message); }
};

export const convertContact = async (id) => {
  const res = await fetch(`${BASE_URL}/contacts/${id}/convert`, { method: 'POST', headers: headers(), body: JSON.stringify({}) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const getContact = async (id) => {
  const res = await fetch(`${BASE_URL}/contacts/${id}`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const getContactTimeline = async (id) => {
  const res = await fetch(`${BASE_URL}/contacts/${id}/timeline`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const getContactCalls = async (id) => {
  const res = await fetch(`${BASE_URL}/contacts/${id}/calls`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.calls;
};

export const logContactCall = async (id, body) => {
  const res = await fetch(`${BASE_URL}/contacts/${id}/calls`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const getContactNotesList = async (id) => {
  const res = await fetch(`${BASE_URL}/contacts/${id}/notes`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.notes;
};

export const addContactNote = async (id, content) => {
  const res = await fetch(`${BASE_URL}/contacts/${id}/notes`, { method: 'POST', headers: headers(), body: JSON.stringify({ content }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const deleteContactNote = async (id, noteId) => {
  const res = await fetch(`${BASE_URL}/contacts/${id}/notes/${noteId}`, { method: 'DELETE', headers: headers() });
  if (!res.ok) { const data = await res.json(); throw new Error(data.message); }
};

// ── Deals ──────────────────────────────────────────────────────────
export const getDeals = async () => {
  const res = await fetch(`${BASE_URL}/deals`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data.deals;
};

export const getDealStats = async () => {
  const res = await fetch(`${BASE_URL}/deals/stats`, { headers: headers() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const createDeal = async (body) => {
  const res = await fetch(`${BASE_URL}/deals`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const updateDeal = async (id, body) => {
  const res = await fetch(`${BASE_URL}/deals/${id}`, { method: 'PATCH', headers: headers(), body: JSON.stringify(body) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const deleteDeal = async (id) => {
  const res = await fetch(`${BASE_URL}/deals/${id}`, { method: 'DELETE', headers: headers() });
  if (!res.ok) { const data = await res.json(); throw new Error(data.message); }
};